// Value-tracking debounce hook — returns the trailing-edge debounced
// value of `value` after `ms` ms of quiescence. Per spec/build-plan.md
// P-029 row + spec/tech-arch.md §4 + §8.
//
// **Consumes `debounce` from `src/lib/debounce.ts`** per D-083's forward
// implication ("`useDebounced` is the third consumer of the debounce
// module"). The trailing-edge coalesce IS the load-bearing feature:
// rapid value changes within the `ms` window collapse to a single
// `setDebounced` after the last change. Cleanup `cancel()` aborts any
// pending invocation on unmount or `ms` change.
//
// **Effect split: `[ms]` builds the debouncer; `[value]` triggers it.**
// Splitting deps reuses one debouncer instance across value changes
// (preserving the helper's internal coalesce) instead of reconstructing
// per change. `useState<T>(value)` initializes the debounced state to
// the input, so first-render consumers see the input immediately (no
// `ms` delay).

import { useEffect, useRef, useState } from 'preact/hooks'

import { debounce } from '../../lib/debounce'

type Trigger<T> = ((value: T) => void) & { cancel: () => void }

export function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState<T>(value)
  const triggerRef = useRef<Trigger<T> | null>(null)

  useEffect(() => {
    const trigger: Trigger<T> = debounce((v: T) => setDebounced(v), ms)
    triggerRef.current = trigger
    return () => {
      trigger.cancel()
      triggerRef.current = null
    }
  }, [ms])

  useEffect(() => {
    triggerRef.current?.(value)
  }, [value])

  return debounced
}
